import { CommonModule } from "@angular/common";
import { Component, Inject } from "@angular/core";
import {
	FormBuilder,
	FormGroup,
	ReactiveFormsModule,
	Validators,
} from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import {
	MAT_DIALOG_DATA,
	MatDialogModule,
	MatDialogRef,
} from "@angular/material/dialog";
import { TranslateModule } from "@ngx-translate/core";
import {
	CreateCups,
	CupsViewModel,
	EnergiaCatalogos,
	UpdateCups,
} from "src/app/_core/models/energia.model";
import { EnergiaService } from "src/app/_core/providers/energia.service";
import { DsSelectComponent } from "src/app/modules/form-controls/components/ds-select/ds-select.component";
import { DsDateComponent } from "src/app/modules/form-controls/components/ds-date/ds-date.component";
import { InfoHintComponent } from "src/app/modules/shared/components/info-hint/info-hint.component";

export interface CupsDialogData {
	centroId: number;
	centroNombre: string;
	catalogos: EnergiaCatalogos;
	/** Si viene, es edición (código y centro inmutables); si no, alta. */
	cups?: CupsViewModel;
}

export type CupsDialogResult = { changed: boolean };

/**
 * Alta/edición de un punto de suministro (CUPS) en diálogo (perfil bespoke
 * `ds-dialog is-complex`, patrón super-centro-dialog). El código y el centro son
 * inmutables en edición (re-asignar = baja + alta). `afterClosed()` devuelve
 * `{ changed: true }` si se guardó.
 */
@Component({
	selector: "app-cups-dialog",
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatIconModule,
		MatDialogModule,
		TranslateModule,
		DsSelectComponent,
		DsDateComponent,
		InfoHintComponent,
	],
	template: `
		<!-- Raíz con .ds: el overlay CDK vive fuera del root de la app (patrón super-centro-dialog). -->
		<div class="ds ds-dialog is-complex ds-dialog--info cud">
			<div class="ds-dialog__head">
				<span class="ds-dialog__icon"><mat-icon>bolt</mat-icon></span>
				<div class="ds-dialog__titles">
					<h2 class="ds-dialog__title">
						{{ (isEdit ? "energia.dialog.editTitle" : "energia.dialog.createTitle") | translate }}
					</h2>
					<p class="ds-dialog__sub">{{ data.centroNombre }}</p>
				</div>
				<button type="button" class="ds-dialog__x" (click)="close()" [attr.aria-label]="'common.cancel' | translate"><mat-icon>close</mat-icon></button>
			</div>

			<div class="ds-dialog__body" [formGroup]="form">
				<!-- Identificación -->
				<h4 class="cud-sec">{{ "energia.dialog.secId" | translate }}</h4>
				<div class="cud-grid">
					<div class="ff-field cud-span2">
						<label>
							{{ "energia.codigo" | translate }}
							<span class="req" *ngIf="!isEdit">*</span>
							<app-info-hint [text]="'energia.codigoInfo' | translate"></app-info-hint>
						</label>
						<div class="ff-box" [class.is-readonly]="isEdit">
							<input
								formControlName="codigo"
								[readonly]="isEdit"
								[placeholder]="'energia.codigoPlaceholder' | translate"
							/>
						</div>
						<small class="ff-hint" *ngIf="isEdit">{{ "energia.codigoInmutable" | translate }}</small>
						<small class="ff-error" *ngIf="invalido('codigo')">{{ "energia.codigoRequerido" | translate }}</small>
					</div>
					<div class="ff-field cud-span2">
						<label>
							{{ "energia.denominacion" | translate }}
							<app-info-hint [text]="'energia.denominacionInfo' | translate"></app-info-hint>
						</label>
						<div class="ff-box">
							<input formControlName="denominacion" [placeholder]="'energia.denominacionPlaceholder' | translate" maxlength="255" />
						</div>
					</div>
					<app-ds-select
						formControlName="estado"
						[label]="'energia.estado' | translate"
						[options]="data.catalogos.estados"
						[allowEmpty]="true"
					></app-ds-select>
				</div>

				<!-- Contrato de suministro -->
				<h4 class="cud-sec">{{ "energia.dialog.secSuministro" | translate }}</h4>
				<div class="cud-grid">
					<app-ds-select
						formControlName="tarifa"
						[label]="'energia.tarifa' | translate"
						[options]="data.catalogos.tarifas"
						[allowEmpty]="true"
					></app-ds-select>
					<app-ds-select
						formControlName="lote"
						[label]="'energia.lote' | translate"
						[options]="data.catalogos.lotes"
						[allowEmpty]="true"
					></app-ds-select>
					<div class="ff-field">
						<label>{{ "energia.tensionKV" | translate }}</label>
						<div class="ff-box">
							<input formControlName="tensionKV" placeholder="400, 3X400/230…" maxlength="20" />
						</div>
					</div>
					<app-ds-date
						formControlName="fechaFinContrato"
						emitAs="iso"
						[label]="'energia.fechaFinContrato' | translate"
						[hint]="'energia.fechaFinContratoHint' | translate"
					></app-ds-date>
				</div>

				<!-- Potencias contratadas -->
				<h4 class="cud-sec">
					{{ "energia.dialog.secPotencias" | translate }}
					<app-info-hint [text]="'energia.potenciasInfo' | translate"></app-info-hint>
				</h4>
				<div class="cud-grid cud-grid-6">
					<div class="ff-field" *ngFor="let p of periodos">
						<label>P{{ p }}</label>
						<div class="ff-box">
							<input type="number" min="0" step="0.01" [formControlName]="'potenciaP' + p" placeholder="kW" />
						</div>
					</div>
				</div>

				<!-- Consumo previsto -->
				<h4 class="cud-sec">
					{{ "energia.dialog.secConsumo" | translate }}
					<app-info-hint [text]="'energia.consumoPrevistoInfo' | translate"></app-info-hint>
				</h4>
				<div class="cud-grid">
					<div class="ff-field cud-span2">
						<label>{{ "energia.consumoPrevistoAnual" | translate }}</label>
						<div class="ff-box">
							<input type="number" min="0" formControlName="consumoPrevistoKwhAnio" placeholder="kWh/año" />
						</div>
					</div>
				</div>
				<div class="cud-grid cud-grid-6">
					<div class="ff-field" *ngFor="let p of periodos">
						<label>P{{ p }}</label>
						<div class="ff-box">
							<input type="number" min="0" [formControlName]="'consumoPrevistoKwhP' + p" placeholder="kWh" />
						</div>
					</div>
				</div>

				<!-- Observaciones -->
				<div class="ff-field cud-obs">
					<label>{{ "energia.observaciones" | translate }}</label>
					<div class="ff-box">
						<textarea formControlName="observaciones" rows="2" maxlength="500"></textarea>
					</div>
				</div>

				<p class="cud-error" *ngIf="errorGuardado">{{ errorGuardado }}</p>
			</div>

			<div class="ds-dialog__footer">
				<button type="button" class="btn btn-ghost btn-sm" (click)="close()">
					{{ "common.cancel" | translate }}
				</button>
				<button type="button" class="btn btn-primary btn-sm" [disabled]="saving || form.invalid" (click)="save()">
					{{ (isEdit ? "common.save" : "common.create") | translate }}
				</button>
			</div>
		</div>
	`,
	styles: [
		`
			.cud {
				display: flex;
				flex-direction: column;
				min-width: 620px;
				max-width: 760px;
				/* Clamp al viewport: la cabecera y el footer quedan SIEMPRE visibles
				   y solo scrollea el cuerpo (evita el doble scroll y el solape con
				   el footer que se veía con el max-height fijo del body). */
				max-height: 82vh;
			}
			.ds-dialog__x {
				margin-left: auto;
				width: 36px;
				height: 36px;
				border-radius: 10px;
				border: 1px solid var(--line, #e6e3dd);
				background: var(--paper, #fff);
				color: var(--ink-3, #8a8a8a);
				display: inline-flex;
				align-items: center;
				justify-content: center;
				cursor: pointer;
				flex: none;
				transition: border-color 0.15s ease, color 0.15s ease;
				&:hover {
					color: var(--ink, #1c1a17);
					border-color: var(--ink-3, #8a8a8a);
				}
				mat-icon {
					font-size: 20px;
					width: 20px;
					height: 20px;
				}
			}
			.ds-dialog__body {
				flex: 1 1 auto;
				min-height: 0;
				overflow: auto;
				padding-bottom: 8px;
			}
			.ds-dialog__head,
			.ds-dialog__footer {
				flex: none;
			}
			.ds-dialog__footer {
				border-top: 1px solid var(--line, #eeece7);
			}
			.cud-sec {
				display: flex;
				align-items: center;
				gap: 6px;
				margin: 14px 0 8px;
				font-size: 11px;
				font-weight: 700;
				letter-spacing: 0.07em;
				text-transform: uppercase;
				color: var(--ink-3, #8a8a8a);
				&:first-child {
					margin-top: 0;
				}
			}
			.cud-grid {
				display: grid;
				grid-template-columns: repeat(2, minmax(0, 1fr));
				gap: 10px 14px;
			}
			.cud-grid-6 {
				grid-template-columns: repeat(6, minmax(0, 1fr));
			}
			.cud-span2 {
				grid-column: span 2;
			}
			.cud-obs {
				margin-top: 12px;
				textarea {
					resize: vertical;
				}
			}
			.cud-error {
				margin: 10px 0 0;
				font-size: 12.5px;
				color: var(--accent, #e30613);
			}
			@media (max-width: 720px) {
				.cud {
					min-width: 0;
				}
				.cud-grid,
				.cud-grid-6 {
					grid-template-columns: repeat(2, minmax(0, 1fr));
				}
			}
		`,
	],
})
export class CupsDialogComponent {
	public readonly periodos = [1, 2, 3, 4, 5, 6];
	public form: FormGroup;
	public saving = false;
	public errorGuardado = "";

	public get isEdit(): boolean {
		return !!this.data.cups;
	}

	constructor(
		private readonly dialogRef: MatDialogRef<CupsDialogComponent, CupsDialogResult>,
		private readonly fb: FormBuilder,
		private readonly energiaService: EnergiaService,
		@Inject(MAT_DIALOG_DATA) public readonly data: CupsDialogData,
	) {
		const c = data.cups;
		this.form = this.fb.group({
			codigo: [{ value: c?.codigo ?? "", disabled: !!c }, [Validators.required, Validators.maxLength(22)]],
			denominacion: [c?.denominacion ?? null],
			estado: [c?.estado ?? null],
			tarifa: [c?.tarifa ?? null],
			lote: [c?.lote ?? null],
			tensionKV: [c?.tensionKV ?? null],
			fechaFinContrato: [c?.fechaFinContrato ?? null],
			potenciaP1: [c?.potenciaP1 ?? null, Validators.min(0)],
			potenciaP2: [c?.potenciaP2 ?? null, Validators.min(0)],
			potenciaP3: [c?.potenciaP3 ?? null, Validators.min(0)],
			potenciaP4: [c?.potenciaP4 ?? null, Validators.min(0)],
			potenciaP5: [c?.potenciaP5 ?? null, Validators.min(0)],
			potenciaP6: [c?.potenciaP6 ?? null, Validators.min(0)],
			consumoPrevistoKwhAnio: [c?.consumoPrevistoKwhAnio ?? null, Validators.min(0)],
			consumoPrevistoKwhP1: [c?.consumoPrevistoKwhP1 ?? null, Validators.min(0)],
			consumoPrevistoKwhP2: [c?.consumoPrevistoKwhP2 ?? null, Validators.min(0)],
			consumoPrevistoKwhP3: [c?.consumoPrevistoKwhP3 ?? null, Validators.min(0)],
			consumoPrevistoKwhP4: [c?.consumoPrevistoKwhP4 ?? null, Validators.min(0)],
			consumoPrevistoKwhP5: [c?.consumoPrevistoKwhP5 ?? null, Validators.min(0)],
			consumoPrevistoKwhP6: [c?.consumoPrevistoKwhP6 ?? null, Validators.min(0)],
			observaciones: [c?.observaciones ?? null],
		});
	}

	public invalido(control: string): boolean {
		const ctrl = this.form.get(control);
		return !!ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched);
	}

	public save(): void {
		if (this.form.invalid || this.saving) {
			this.form.markAllAsTouched();
			return;
		}
		this.saving = true;
		this.errorGuardado = "";
		const value = this.form.getRawValue();

		const peticion = this.isEdit
			? this.energiaService.updateCups({ ...value, id: this.data.cups!.id } as UpdateCups)
			: this.energiaService.createCups({ ...value, centroID: this.data.centroId } as CreateCups);

		peticion.subscribe({
			next: () => this.dialogRef.close({ changed: true }),
			error: (err) => {
				this.saving = false;
				// El back devuelve { message, detail } en errores de validación (400)
				this.errorGuardado = err?.error?.detail || err?.error?.message || "Error";
			},
		});
	}

	public close(): void {
		this.dialogRef.close({ changed: false });
	}
}
